// =====================================================================
// src/lib/troca_de_imobiliaria.js — trocar a imobiliária do quarto (0102)
//
// Hugo, 02/11/2026 (PLANO da 0102 e o esboço aprovado): em Configurações →
// quarto, o seletor de imobiliária TRAVA quando o quarto já tem uma, e a
// mudança passa pelo botão "Trocar imobiliária", que abre um modal com as
// reservas do quarto de hoje em diante — cada uma decidida: fica com a
// antiga (só a que já começou), passa para a nova (com o valor e o
// lançamento) ou é cancelada (motivo "troca de imobiliária"; não com hóspede
// dentro). Vale hoje.
//
// O MdM não tem tela de troca (lá só há uma imobiliária). O modal é montado
// com os componentes de lá — modal, form-row, tabela, badge, alerta e os
// botões btn/btn-primary/btn-secondary/btn-sm — e a escolha de cada linha é
// um grupo de três btn-sm (o selecionado em btn-primary), que é o que o
// esboço desenha.
//
// Funções PURAS: nada de DOM, nada de rede — testadas em
// scripts/teste_tela.mjs. Quem decide é o banco (0102):
//   previa_da_troca_de_imobiliaria(p_unidade)  → as reservas e as `opcoes`
//     que cada uma admite. A tela NÃO recalcula a regra: usa as opções que
//     o banco mandou (uma regra só, num lugar só).
//   trocar_imobiliaria_do_quarto(p_unidade, p_de, p_para, p_decisoes)
// O hotel nunca vê valor: nada aqui fala de quanto (INVARIANTE 2).
// =====================================================================

export const OPCOES = Object.freeze(['fica', 'passa', 'cancela']);

// Quem vê o botão: a Administração do hotel e o Administrador do Sistema, com
// Configurações em editar (a mesma porta de app.cliente_que_troca_imobiliaria).
export function possoTrocar(contexto, nivelConfiguracoes) {
  const c = contexto || {};
  if (nivelConfiguracoes !== 'editar') return false;
  if (c.usuario && c.usuario.sistema) return true;
  if (c.imobiliaria) return false;
  return !!(c.papel && c.papel.chave === 'administracao');
}

// O seletor de Configurações trava quando o quarto já tem imobiliária. O
// quarto novo e o quarto sem imobiliária escolhem a primeira por ali.
export function seletorTravado(quarto) {
  return !!(quarto && quarto.imobiliaria_id);
}

// O que vai para salvar_unidade como p_imobiliaria_id: com o seletor
// travado, a que o quarto já tem (o salvar só corrige número/andar/leitos);
// sem ele, o que estiver escolhido (vazio = nenhuma).
export function imobiliariaParaSalvar(quarto, valorDoSeletor) {
  if (seletorTravado(quarto)) return quarto.imobiliaria_id;
  return valorDoSeletor ? valorDoSeletor : null;
}

// As opções do seletor "Nova imobiliária": as ativas, menos a atual, por nome.
export function imobiliariasParaTroca(imobiliarias, atual) {
  return (imobiliarias || [])
    .filter(a => a && a.ativo && a.id !== atual)
    .slice()
    .sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
}

const doisDigitos = n => String(n).padStart(2, '0');
// "05/12" (do ISO "2026-12-05")
export function diaMes(iso) {
  if (!iso) return '';
  const [, m, d] = String(iso).split('-');
  return `${d}/${m}`;
}
// "hoje, 01/12/2026"
export function valeAPartirDe(hojeIso) {
  if (!hojeIso) return 'hoje';
  const [a, m, d] = String(hojeIso).split('-');
  return `hoje, ${doisDigitos(d)}/${doisDigitos(m)}/${a}`;
}

const PLATAFORMA = { booking: 'Booking', airbnb: 'Airbnb', expedia: 'Expedia', decolar: 'Decolar', direto: 'Direta', direta: 'Direta' };
// "Booking 4471", "Airbnb HM3K", "Direta 2026-0612" (o esboço). Sem número,
// a plataforma; sem nada, "Reserva".
export function rotuloDaReserva(r) {
  const p = String((r && r.plataforma) || '').trim();
  const nomeP = p ? (PLATAFORMA[p.toLowerCase()] || p.charAt(0).toUpperCase() + p.slice(1)) : '';
  const num = String((r && (r.numero_ota || r.numero)) || '').trim();
  if (nomeP && num) return `${nomeP} ${num}`;
  if (num) return `Reserva ${num}`;
  return nomeP || 'Reserva';
}
export function periodoDaReserva(r) {
  return `${diaMes(r.check_in)} → ${diaMes(r.check_out)}`;
}

// O texto de cada botão da linha
export function rotuloDaOpcao(op, antiga, nova) {
  if (op === 'fica') return `Fica com a ${antiga || 'atual'}`;
  if (op === 'passa') return `Passa para a ${nova || 'nova'}`;
  return 'Cancelar';
}

// A frase debaixo dos botões: por que um deles não está lá, ou o que a
// escolha vai fazer.
export function notaDaReserva(r, decisao) {
  const opcoes = (r && r.opcoes) || [];
  const notas = [];
  if (decisao === 'cancela') notas.push('será cancelada com o motivo "troca de imobiliária"');
  else if (r && r.hospede_dentro && !opcoes.includes('cancela')) notas.push('com hóspede dentro não se cancela');
  if (decisao === 'passa' && r && r.vaga_pedida) notas.push('o pedido de vaga cai (a vaga é da imobiliária atual)');
  return notas.join(' · ');
}

// O estado do modal: a nova escolhida e uma decisão válida para cada reserva.
// `decisoes` é { reserva_id: 'fica' | 'passa' | 'cancela' }.
export function estadoDaTroca({ reservas, decisoes, nova }) {
  const lista = reservas || [];
  const d = decisoes || {};
  const faltam = lista.filter(r => !(r.opcoes || []).includes(d[r.reserva_id])).length;
  const conta = { fica: 0, passa: 0, cancela: 0 };
  for (const r of lista) if ((r.opcoes || []).includes(d[r.reserva_id])) conta[d[r.reserva_id]]++;
  const erros = [];
  if (!nova) erros.push('Escolha a nova imobiliária');
  if (faltam) erros.push(faltam === 1 ? 'Falta decidir 1 reserva' : `Faltam decidir ${faltam} reservas`);
  return { pronta: !erros.length, faltam, conta, erros };
}

// O p_decisoes que vai para o banco: só as reservas da prévia, cada uma com
// uma opção que ela admite. O que estiver fora disso não vai (e, se faltar
// alguma, a tela não chama — `estadoDaTroca().pronta`).
export function decisoesParaEnviar(reservas, decisoes) {
  const d = decisoes || {};
  const out = {};
  for (const r of reservas || []) if ((r.opcoes || []).includes(d[r.reserva_id])) out[r.reserva_id] = d[r.reserva_id];
  return out;
}

// O alerta do esboço: o que a troca faz além das reservas
export function avisoDaTroca({ quarto, antiga, nova }) {
  const n = nova || 'nova imobiliária';   // "chega à nova imobiliária", "para a nova imobiliária"
  return `O Quarto ${quarto} chega à ${n} sem proprietário; o cadastro do proprietário continua na ${antiga}. `
    + `Os chamados ainda abertos do Quarto ${quarto} passam a aparecer para a ${n}. `
    + `Se uma reserva que passa tinha pedido de vaga ainda não liberado, o pedido cai, porque a vaga é da ${antiga}; a ${n} pede uma vaga dela.`;
}

// O título da seção das reservas: "Reservas do Quarto 801 de hoje em diante (3)"
export function tituloDasReservas(quarto, n) {
  return `Reservas do Quarto ${quarto} de hoje em diante (${n})`;
}

// O aviso depois da troca, a partir do que o banco devolveu
export function resumoDaTroca(resp) {
  const r = resp || {};
  const partes = [];
  if (r.passaram) partes.push(`${r.passaram} passou(aram)`);
  if (r.ficaram) partes.push(`${r.ficaram} ficou(aram) com a ${r.de}`);
  if (r.canceladas) partes.push(`${r.canceladas} cancelada(s)`);
  const reservas = partes.length ? ` Reservas: ${partes.join(', ')}.` : ' Nenhuma reserva de hoje em diante.';
  return `Quarto ${r.quarto}: agora é da ${r.para}.${reservas}`;
}
